import { AddIncomeFlow, type Income } from '@fruitful/member-profile';
import { Redirect, router, useLocalSearchParams } from 'expo-router';
import { ScreenContainer, useToastController } from 'smoothie';

import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { captureException } from '../../logger/Logger';
import type { User } from '../../user/types/User';
import useGetMembershipIncomes from '../hooks/useGetMembershipIncomes';
import useUpsertMembershipIncome from '../hooks/useUpsertMembershipIncome';
import {
    MemberProfileLogger,
    transformUserToMemberProfileMembership,
} from '../utils/memberProfileUtils';

export const UpsertIncome = () => {
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('Profile/AddIncome');

    const toast = useToastController();
    const authedUser = useAuthedUser();
    const { incomeId, userId } = useLocalSearchParams<{
        incomeId?: string;
        userId?: string;
    }>();

    // Use provided userId or fallback to authenticated user. Find the target user.
    const targetUserId = userId ?? authedUser.id;
    const targetUser = [authedUser, authedUser.partnerMember].find(
        (user) => user?.id === targetUserId,
    );

    const { data: membershipIncomes, refetch: refetchMembershipIncomes } =
        useGetMembershipIncomes({});

    const membership = transformUserToMemberProfileMembership(authedUser);

    // Should not happen, but added as a defensive check.
    if (targetUser == null) {
        captureException(
            new Error('Target user not found for upsert income page'),
        );
        return <Redirect href="/profile/income" />;
    }

    const { mutateAsync: upsertMembershipIncomes } =
        useUpsertMembershipIncome();

    // Find existing income data if editing
    const existingIncome =
        incomeId != null
            ? membershipIncomes?.find((income) => income.id === incomeId)
            : undefined;

    const onAddIncomeComplete = async (data: Income) => {
        const upsertDto = {
            ...data,
            membershipId: membership.id,
            ...(incomeId != null && { id: incomeId }), // Include ID if editing
        };

        try {
            await upsertMembershipIncomes({
                body: [upsertDto],
            });

            void refetchMembershipIncomes();

            toast.show(
                incomeId != null
                    ? 'Income updated successfully'
                    : 'Income added successfully',
                {
                    duration: 2000,
                },
            );

            router.back();
        } catch (error) {
            captureException(error);
            toast.show(
                incomeId != null
                    ? 'Failed to update income'
                    : 'Failed to add income',
                {
                    duration: 2000,
                },
            );
        }
    };

    const onError = (exception: Error) => {
        captureException(exception);
        toast.show('Something went wrong!', { duration: 2000 });
    };

    return (
        <ScreenContainer>
            <AddIncomeFlow
                context="settings"
                currentUser={targetUser as User} // Set currentUser as targetUser so they are set as the income owner.
                existingData={existingIncome ?? {}}
                onComplete={onAddIncomeComplete}
                onExit={() => {
                    if (router.canGoBack()) {
                        router.back();
                    } else {
                        router.navigate('/profile/financial-profile');
                    }
                }}
                onError={onError}
                membership={membership}
                logger={MemberProfileLogger}
            />
        </ScreenContainer>
    );
};
